import { Injectable } from '@angular/core';

/**
 * Genera un `device_id` deterministico da fingerprint browser/ambiente.
 * Stesso browser + stessa macchina → stesso ID anche dopo cancellazione da localStorage.
 * Macchina o browser diversi → ID diversi.
 *
 * Non è un ID hardware reale (non disponibile in web): è un hash di segnali stabili.
 * NON gestisce salvataggio/stato — responsabilità di AppStore.
 *
 * Algoritmo allineato a ERP-adv (`device-id.service.ts`) per device_name SSO coerenti.
 */
/** Formato fingerprint corrente: `fp_` + 16 hex. */
const DEVICE_ID_FINGERPRINT_RE = /^fp_[0-9a-f]{16}$/i;

@Injectable({
  providedIn: 'root'
})
export class DeviceIdService {
  /**
   * True solo per fingerprint `fp_…`.
   * Placeholder (es. letterale `device_id`), UUID o numerici legacy → false (vanno rigenerati).
   */
  isValidDeviceId(value: string | null | undefined): boolean {
    return typeof value === 'string' && DEVICE_ID_FINGERPRINT_RE.test(value.trim());
  }

  /**
   * Fingerprint stabile (prefisso `fp_` + 16 hex).
   */
  generateDeviceId(): string {
    const payload = DeviceIdService.collectFingerprintPayload();
    const hash = DeviceIdService.fnv1a64Hex(payload);

    return `fp_${hash}`;
  }

  private static collectFingerprintPayload(): string {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const scr = typeof screen !== 'undefined' ? screen : undefined;
    const deviceMemory =
      nav && 'deviceMemory' in nav ? String((nav as Navigator & { deviceMemory?: number }).deviceMemory ?? '') : '';

    const parts = [
      nav?.userAgent ?? '',
      nav?.language ?? '',
      Array.isArray(nav?.languages) ? nav.languages.join(',') : '',
      nav?.platform ?? '',
      String(nav?.hardwareConcurrency ?? ''),
      deviceMemory,
      String(nav?.maxTouchPoints ?? ''),
      String(scr?.width ?? ''),
      String(scr?.height ?? ''),
      String(scr?.colorDepth ?? ''),
      String(scr?.pixelDepth ?? ''),
      DeviceIdService.resolveTimeZone(),
      DeviceIdService.canvasFingerprint(),
      DeviceIdService.webglFingerprint()
    ];

    return parts.join('|');
  }

  private static resolveTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
      return '';
    }
  }

  private static canvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');

      canvas.width = 200;
      canvas.height = 50;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return '';
      }

      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(0, 0, 120, 50);
      ctx.fillStyle = '#069';
      ctx.fillText('pn-device-fp', 2, 2);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('pn-device-fp', 4, 18);

      return canvas.toDataURL();
    } catch {
      return '';
    }
  }

  private static webglFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl =
        canvas.getContext('webgl') ||
        (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);

      if (!gl) {
        return '';
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      if (!debugInfo) {
        return '';
      }

      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

      return `${vendor}~${renderer}`;
    } catch {
      return '';
    }
  }

  /** FNV-1a 64-bit → 16 hex (sync, senza SubtleCrypto). */
  private static fnv1a64Hex(input: string): string {
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;

    for (let i = 0; i < input.length; i++) {
      hash ^= BigInt(input.charCodeAt(i));
      hash = (hash * prime) & 0xffffffffffffffffn;
    }

    return hash.toString(16).padStart(16, '0');
  }
}
